import {
  FrameReportItem,
  PanelCommandDefinition,
  PanelCommandItem,
  PanelCommandKey,
  PanelCommandVisualStatus,
  PanelEquipmentCounters,
  PanelFeeder,
  PanelFeederStatus,
  PanelFilters,
  PanelLoadingStatus,
  PanelProgress
} from "../interfaces/panel-alerts.interface";
import {
  buildEmptyLoadings,
  COMMAND_BY_FRAME_NAME,
  COMMAND_BY_SOCKET_TYPE,
  PANEL_POWER_COMMANDS
} from "../constants/panel-alerts.constants";

/** Estados de comando que cuentan como proceso terminado, con exito o sin el. */
const FINISHED_STATUSES: PanelLoadingStatus[] = ["done", "error"];

/**
 * Resuelve que comando cierra una trama del socket.
 *
 * Las tramas ON y OFF no bastan por si solas: el comando real depende de si el
 * equipo reporta `power` o `spin_time`, porque el mismo tipo de trama responde
 * tanto al encendido como a la rotacion.
 * @param data Contenido de `socket.data`.
 * @returns Comando que la trama cierra, o cadena vacia si no corresponde a ninguno.
 */
export function commandFromSocket(data: any): PanelCommandKey | "" {
  if (!data || !data.type) {
    return "";
  }
  if (data.type === "ON" || data.type === "OFF") {
    if (Object.prototype.hasOwnProperty.call(data, "power")) {
      return data.power ? "turn_on" : "turn_off";
    }
    if (Object.prototype.hasOwnProperty.call(data, "spin_time")) {
      return data.spin_time ? "turn_on_spin_time" : "turn_off_spin_time";
    }
  }
  return COMMAND_BY_SOCKET_TYPE[data.type] || "";
}

/**
 * Indica si un comando ya se envio, es decir si dejo rastro en algun equipo.
 * @param feeders Equipos del panel.
 * @param key Comando a comprobar.
 * @returns `true` si al menos un equipo tiene estado para ese comando.
 */
export function isCommandSent(feeders: PanelFeeder[], key: PanelCommandKey): boolean {
  return feeders.some((feeder) => feeder.loadings[key] !== "");
}

/**
 * Lista los comandos que ya se enviaron en el lote actual.
 * @param feeders Equipos del panel.
 * @param definitions Definiciones de comando disponibles.
 * @returns Comandos con rastro, excluidos los que nunca derivan estado.
 */
export function sentCommands(
  feeders: PanelFeeder[],
  definitions: PanelCommandDefinition[]
): PanelCommandKey[] {
  return definitions
    .filter((definition) => !definition.always_idle && isCommandSent(feeders, definition.key))
    .map((definition) => definition.key);
}

/**
 * Cuenta cuantos procesos termino un equipo sobre los comandos enviados.
 *
 * Un comando en espera no suma al numerador: solo cuentan los que ya cerraron,
 * hayan respondido bien o con error.
 * @param feeder Equipo a evaluar.
 * @param sent Comandos enviados en el lote actual.
 * @returns Procesos terminados y total de comandos que aplican al equipo.
 */
export function countFeederProgress(
  feeder: PanelFeeder,
  sent: PanelCommandKey[]
): { done: number; total: number } {
  const applicable = sent.filter((key) => feeder.loadings[key] !== "");
  const done = applicable.filter(
    (key) => FINISHED_STATUSES.indexOf(feeder.loadings[key]) >= 0
  ).length;
  return { done, total: applicable.length };
}

/**
 * Determina a cuantos equipos aplica un comando.
 *
 * Encender y apagar solo se envian al subconjunto que lo necesita, asi que su
 * total no es el numero de equipos seleccionados.
 * @param feeders Equipos del panel.
 * @param key Comando a evaluar.
 * @returns Numero de equipos a los que aplica.
 */
export function countCommandTargets(feeders: PanelFeeder[], key: PanelCommandKey): number {
  if (PANEL_POWER_COMMANDS.indexOf(key) < 0) {
    return feeders.length;
  }
  return feeders.filter((feeder) =>
    key === "turn_on" ? !feeder.status_power : feeder.status_power
  ).length;
}

/**
 * Traduce el estado interno de un comando al color del mockup.
 * @param feeders Equipos del panel.
 * @param definition Definicion del comando.
 * @returns Gris sin enviar, azul esperando respuesta, verde proceso terminado.
 */
function visualStatus(
  feeders: PanelFeeder[],
  definition: PanelCommandDefinition
): PanelCommandVisualStatus {
  if (definition.always_idle || !isCommandSent(feeders, definition.key)) {
    return "idle";
  }
  const waiting = feeders.some((feeder) => feeder.loadings[definition.key] === "loading");
  return waiting ? "waiting" : "finished";
}

/**
 * Calcula el estado de cada boton del panel de comandos.
 * @param feeders Equipos del panel.
 * @param definitions Definiciones de comando visibles para el usuario.
 * @returns Comandos con su total, sus respuestas y su color.
 */
export function computeCommandItems(
  feeders: PanelFeeder[],
  definitions: PanelCommandDefinition[]
): PanelCommandItem[] {
  return definitions.map((definition) => {
    const eligibleCount = countCommandTargets(feeders, definition.key);
    /* Una vez enviado el comando, el total del contador refleja cuantos lo
       recibieron, no cuantos siguen elegibles. Asi turn_off sigue mostrando
       "1/1" aunque el equipo ya cambio a apagado y su total elegible sea 0. */
    const sent = isCommandSent(feeders, definition.key);
    const total = sent
      ? feeders.filter((feeder) => feeder.loadings[definition.key] !== "").length
      : eligibleCount;
    const answered = feeders.filter(
      (feeder) => FINISHED_STATUSES.indexOf(feeder.loadings[definition.key]) >= 0
    ).length;
    return {
      ...definition,
      total,
      answered,
      status: visualStatus(feeders, definition),
      disabled: eligibleCount === 0
    };
  });
}

/**
 * Resume el estado de un equipo respecto a los comandos enviados.
 *
 * Las categorias son excluyentes y se evaluan en este orden: mientras falte una
 * respuesta el equipo esta en espera, aunque ya haya fallado algun comando.
 * @param feeder Equipo a evaluar.
 * @param sent Comandos enviados en el lote actual.
 * @returns Estado resumido del equipo; "idle" si no se le envio nada.
 */
export function feederStatus(feeder: PanelFeeder, sent: PanelCommandKey[]): PanelFeederStatus {
  const applicable = sent.filter((key) => feeder.loadings[key] !== "");
  if (applicable.length === 0) {
    return "idle";
  }
  if (applicable.some((key) => feeder.loadings[key] === "loading")) {
    return "waiting";
  }
  if (applicable.some((key) => feeder.loadings[key] === "error")) {
    return "with_error";
  }
  return "answered";
}

/**
 * Reparte las respuestas esperadas del lote (un comando por equipo) entre
 * respondidas, en espera y con error. Si a 40 equipos se les mandan tres
 * comandos se esperan 120 respuestas, y cada una cuenta por separado.
 *
 * Encender y Apagar ocupan un solo lugar por equipo, igual que en
 * `computeProgress`, para que los tres contadores sumen su mismo total.
 * @param feeders Equipos del panel.
 * @param sent Comandos enviados en el lote actual.
 * @returns Contadores del pie del panel.
 */
export function computeEquipmentCounters(
  feeders: PanelFeeder[],
  sent: PanelCommandKey[]
): PanelEquipmentCounters {
  const counters: PanelEquipmentCounters = { answered: 0, waiting: 0, with_error: 0 };
  const nonPowerKeys = sent.filter((key) => PANEL_POWER_COMMANDS.indexOf(key) < 0);
  const hasPower = sent.some((key) => PANEL_POWER_COMMANDS.indexOf(key) >= 0);

  feeders.forEach((feeder) => {
    const statuses = nonPowerKeys.map((key) => feeder.loadings[key]);
    if (hasPower) {
      statuses.push(powerSlotStatus(feeder));
    }
    statuses.forEach((status) => {
      if (status === "loading") {
        counters.waiting = counters.waiting + 1;
      }
      if (status === "done") {
        counters.answered = counters.answered + 1;
      }
      if (status === "error") {
        counters.with_error = counters.with_error + 1;
      }
    });
  });

  return counters;
}

/**
 * Estado del lugar de potencia de un equipo: en espera mientras Encender o
 * Apagar sigan sin respuesta; si no, respondido si alguno cerro bien.
 * @param feeder Equipo a evaluar.
 * @returns Estado del lugar de potencia; vacio si no se le envio ninguno.
 */
function powerSlotStatus(feeder: PanelFeeder): PanelLoadingStatus {
  const statuses = PANEL_POWER_COMMANDS.map((key) => feeder.loadings[key]).filter(
    (status) => status !== ""
  );
  if (statuses.length === 0) {
    return "";
  }
  if (statuses.indexOf("loading") >= 0) {
    return "loading";
  }
  return statuses.indexOf("done") >= 0 ? "done" : "error";
}

/**
 * Calcula el progreso de envio de comandos del lote actual.
 *
 * Encender y Apagar se tratan como un unico slot de potencia por equipo:
 * un alimentador que recibio ambos comandos cuenta UNA sola vez en el total,
 * y ese slot se considera terminado cuando ningun comando de potencia sigue en
 * estado "loading" (el ultimo enviado ya cerro).
 *
 * El numerador lo aporta el backend cuando hay reporte de frames, porque el
 * front solo sabe que la peticion se acepto, no cuantos mensajes salieron de
 * verdad. Mientras no llega el reporte se estima con los procesos terminados.
 * @param feeders Equipos del panel.
 * @param sent Comandos enviados en el lote actual.
 * @param emittedFrames Frames que el backend confirma haber emitido, si se conoce.
 * @returns Enviados, total y porcentaje.
 */
export function computeProgress(
  feeders: PanelFeeder[],
  sent: PanelCommandKey[],
  emittedFrames?: number
): PanelProgress {
  const nonPowerKeys = sent.filter((k) => PANEL_POWER_COMMANDS.indexOf(k) < 0);
  const hasPower = sent.some((k) => PANEL_POWER_COMMANDS.indexOf(k) >= 0);

  /* Total: comandos no-potencia por equipos que los recibieron + equipos con
     cualquier comando de potencia (contados una sola vez). */
  const total =
    nonPowerKeys.reduce(
      (acc, key) => acc + feeders.filter((f) => f.loadings[key] !== "").length,
      0
    ) +
    (hasPower
      ? feeders.filter((f) => f.loadings.turn_on !== "" || f.loadings.turn_off !== "").length
      : 0);

  const finished = feeders.reduce((acc, feeder) => {
    /* Comandos no-potencia terminados para este equipo. */
    const nonPowerDone = nonPowerKeys.filter(
      (k) => feeder.loadings[k] !== "" && FINISHED_STATUSES.indexOf(feeder.loadings[k]) >= 0
    ).length;

    /* Slot de potencia: terminado cuando ningun comando de potencia enviado
       sigue en "loading". Si ambos (turn_on y turn_off) se mandaron y el ultimo
       ya cerro, el slot cuenta como 1. */
    let powerDone = 0;
    if (hasPower) {
      const hasTurnOn = feeder.loadings.turn_on !== "";
      const hasTurnOff = feeder.loadings.turn_off !== "";
      if (hasTurnOn || hasTurnOff) {
        const powerLoading =
          (hasTurnOn && feeder.loadings.turn_on === "loading") ||
          (hasTurnOff && feeder.loadings.turn_off === "loading");
        if (!powerLoading) {
          powerDone = 1;
        }
      }
    }

    return acc + nonPowerDone + powerDone;
  }, 0);

  const sentCount = typeof emittedFrames === "number" ? Math.min(emittedFrames, total) : finished;
  const percentage = total > 0 ? Math.round((sentCount * 100) / total) : 0;

  return { sent: sentCount, total, percentage };
}

/**
 * Busca la respuesta mas reciente recibida por cualquier equipo.
 * @param feeders Equipos del panel.
 * @returns Fecha ISO de la ultima respuesta, o cadena vacia si aun no hay ninguna.
 */
export function lastResponseAt(feeders: PanelFeeder[]): string {
  return feeders.reduce((latest, feeder) => {
    const current = feeder.last_response_at || "";
    return current > latest ? current : latest;
  }, "");
}

/**
 * Indica si un equipo encaja con el filtro de estado seleccionado.
 * @param feeder Equipo a evaluar.
 * @param status Estado buscado.
 * @returns `true` si alguno de sus comandos esta en ese estado.
 */
function matchesStatus(feeder: PanelFeeder, status: string): boolean {
  if (!status) {
    return true;
  }
  return Object.keys(feeder.loadings).some((key) => feeder.loadings[key] === status);
}

/**
 * Aplica los filtros de nombre y estado sobre la lista de equipos.
 * @param feeders Equipos del panel.
 * @param filters Filtros activos.
 * @returns Equipos visibles, en el mismo orden que la lista original.
 */
export function applyFilters(feeders: PanelFeeder[], filters: PanelFilters): PanelFeeder[] {
  const name = (filters.name || "").toLowerCase();
  return feeders.filter((feeder) => {
    const matchesName = name ? (feeder.name || "").toLowerCase().indexOf(name) > -1 : true;
    return matchesName && matchesStatus(feeder, filters.statusFeeders);
  });
}

/**
 * Traduce el resultado que reporta el backend para un frame al estado interno
 * del panel. Una respuesta recibida fuera de tiempo se considera valida.
 * @param result Resultado del intento segun el backend.
 * @returns Estado equivalente del comando.
 */
export function statusFromFrameResult(result: number): PanelLoadingStatus {
  if (result === -1) {
    return "loading";
  }
  if (result === 1) {
    return "error";
  }
  return "done";
}

/**
 * Aplica el reporte de frames del backend sobre la lista de equipos.
 *
 * El socket es mas rapido que el sondeo, asi que el reporte solo puede adelantar
 * el estado de un comando, nunca devolverlo a "en espera". Eso evita que la
 * interfaz parpadee por el desfase entre ambas fuentes.
 * @param feeders Equipos del panel.
 * @param report Frames emitidos que devolvio el backend.
 * @returns Lista nueva con los estados y numeros de intento actualizados.
 */
export function mergeFrameReport(feeders: PanelFeeder[], report: FrameReportItem[]): PanelFeeder[] {
  if (report.length === 0) {
    return feeders;
  }

  return feeders.map((feeder) => {
    const frames = report.filter(
      (item) => item.feeder_id === feeder.id || item.address === feeder.mac
    );
    if (frames.length === 0) {
      return feeder;
    }

    const loadings = { ...feeder.loadings };
    const attempts: { [key: string]: number } = {};

    frames.forEach((frame) => {
      const key = COMMAND_BY_FRAME_NAME[frame.frame_name];
      if (!key) {
        return;
      }
      const last = frame.expected_response[frame.expected_response.length - 1];
      if (!last) {
        return;
      }
      const reported = statusFromFrameResult(last.result);
      /* Solo se adelanta el estado: si el socket ya cerro el comando, se respeta. */
      const alreadyFinished = FINISHED_STATUSES.indexOf(loadings[key]) >= 0;
      if (!alreadyFinished) {
        loadings[key] = reported;
      }
      attempts[key] = last.attempt_no;
    });

    const alerts = feeder.alerts.map((alert) => {
      const attempt = attempts[alert.alert];
      return typeof attempt === "number" ? { ...alert, attempt_no: attempt } : alert;
    });

    return { ...feeder, loadings, alerts };
  });
}

/**
 * Copia un alimentador en profundidad hasta donde el panel lo muta.
 *
 * Hace falta porque `GlobalFunction.setDataFeeders` escribe sobre el array y
 * sobre `alerts` que recibe; trabajando sobre una copia, el estado del
 * componente solo cambia cuando este lo decide.
 * @param feeder Alimentador a copiar.
 * @returns Copia independiente del original.
 */
export function cloneFeeder(feeder: PanelFeeder): PanelFeeder {
  return {
    ...feeder,
    loadings: { ...feeder.loadings },
    alerts: (feeder.alerts || []).map((alert) => ({ ...alert }))
  };
}

/**
 * Normaliza un alimentador recien recibido para que el panel pueda operarlo.
 * @param feeder Alimentador tal y como llega del componente que abre el panel.
 * @returns Alimentador con estado de comandos, alertas y seleccion inicializados.
 */
export function normalizeFeeder(feeder: any): PanelFeeder {
  return {
    ...feeder,
    loadings: buildEmptyLoadings(),
    alerts: [],
    condition: true,
    text_condition: "",
    checked: false,
    collapse_feeders: false
  };
}
